import { isEmpty, isObject, toPairs } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type FieldErrors, useFormState } from 'react-hook-form';
import { useLocation } from 'react-router';
import { useInvalidFormEvent } from '../augmented/useInvalidFormEvent.tsx';
import { isLeafFieldError, type LeafError } from './LeafError.tsx';
import { AlertMessage } from './message/AlertMessage.tsx';

export interface IFormErrorsAlertProps {
  style?: React.CSSProperties;
}

export function errorsToViewableList(
  errors: FieldErrors<any> | undefined,
  path: Array<string | number> = [],
): LeafError[] {
  if (!errors) return [];
  if (isLeafFieldError(errors)) {
    return [{ path, error: errors }];
  }
  const flatErrors = toPairs(errors).flatMap<LeafError>(([key, error]) => {
    if (isLeafFieldError(error)) {
      return [{ path: [...path, key], error }] as LeafError[];
    }
    if (Array.isArray(error)) {
      return error.flatMap((e, i) =>
        errorsToViewableList(e, [...path, key, i]),
      );
    }
    if (isObject(error) && !isEmpty(error)) {
      // TODO: Verificare l'aggiornamento di react-hook-form
      return errorsToViewableList(error as any, [...path, key]);
    }

    return [];
  });
  return flatErrors;
}

/**
 * Mostra un Alert con gli errori di compilazione al momento del Submit
 * del form.
 */
export function FormErrorsAlert(props: IFormErrorsAlertProps) {
  const { errors: formErrors } = useFormState();

  const location = useLocation();

  // Forzo la rimozione degli errori quando cambio pagina
  // Utile nell'edit dei moduli dalla domanda dove al cambio di modulo
  // il componente non viene smontato e l'errore rimane visibile.
  useEffect(() => {
    location; // NOSONAR
    setErrors(null);
    setVisible(false);
  }, [location]);

  // Non mostriamo gli ultimi errori, ma solo quelli al momento della validazione.
  // Eventuali errori successivi non verranno mostrati.
  // TODO Decidere se mostrare gli errori successivi o solo quelli al momento della validazione.
  const [errors, setErrors] = useState<FieldErrors<any> | null>(formErrors);
  const [visible, setVisible] = useState<boolean>(false);
  useInvalidFormEvent(event => {
    if (event.type === 'valid') {
      setErrors(null);
      setVisible(false);
    }

    if (event.type === 'invalid') {
      setVisible(true);
      setErrors(event.errors);
    }
  });

  // Gestione visualizzazione degli errori di validazione
  const viewErrors = useMemo(() => {
    return errorsToViewableList(errors ?? {});
  }, [errors]);

  const handleAfterClose = useCallback(() => {
    setVisible(false);
  }, []);

  if (!visible || isEmpty(errors)) return null;

  return (
    <AlertMessage
      style={props.style}
      afterClose={handleAfterClose}
      errors={viewErrors.map(e => e.error.message!)}
    />
  );
}
